import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  House, ListChecks, Package, Plus, Bell, SignOut, User, Pill, GearSix, ChartBar, Receipt, Notepad, MapPin, Lightning, Storefront, UserCheck, Buildings
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "./ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Layout({ children }) {
  const { user, logout, activeOrg, activeMembership, isOrgAdmin, setActiveOrg } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const isAdmin = user?.role === "master_admin";
  const memberships = user?.memberships || [];
  const hasMultipleOrgs = memberships.length > 1;

  useEffect(() => {
    if (!user) return;
    const fetchNotifs = async () => {
      try {
        const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
        setUnread(data.unread_count);
      } catch { /* ignore */ }
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    const onRead = () => setUnread(0);
    window.addEventListener("notifications-read", onRead);
    return () => {
      clearInterval(interval);
      window.removeEventListener("notifications-read", onRead);
    };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = isAdmin
    ? [
        { path: "/", label: "Dashboard", icon: House },
        { path: "/admin/orders", label: "Orders", icon: ListChecks },
        { path: "/admin/products", label: "Products", icon: Pill },
        { path: "/admin/approvals", label: "Approvals", icon: UserCheck },
        { path: "/admin/orgs", label: "Clinics", icon: Buildings },
        { path: "/admin/users", label: "Users", icon: User },
        { path: "/admin/analytics", label: "Analytics", icon: ChartBar },
        { path: "/admin/statements", label: "Statements", icon: Receipt },
        { path: "/admin/settings", label: "Settings", icon: GearSix },
      ]
    : [
        { path: "/", label: "Dashboard", icon: House },
        { path: "/orders/new", label: "New Order", icon: Plus },
        { path: "/orders", label: "Orders", icon: ListChecks },
        ...(isOrgAdmin ? [
          { path: "/org/members", label: "Members", icon: User },
          { path: "/org/addresses", label: "Addresses", icon: MapPin },
        ] : []),
      ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="app-layout">
      {/* Top Nav */}
      <nav className="sticky top-0 z-50 w-full border-b border-zinc-200 bg-white/90 backdrop-blur-xl" data-testid="top-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-7 h-7 bg-[#0055FF] flex items-center justify-center">
              <span className="text-white font-black text-xs font-[Chivo]">A</span>
            </div>
            <span className="font-[Chivo] font-bold text-zinc-950 text-sm tracking-tight hidden sm:inline">
              AMCAL NEWMARKET
            </span>
          </Link>

          {/* Workspace switcher (clinic users with active membership) */}
          {!isAdmin && activeOrg && (
            hasMultipleOrgs ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-zinc-200 hover:border-zinc-400 transition-colors" data-testid="workspace-switcher">
                    <Buildings size={14} className="text-[#0055FF]" weight="fill" />
                    <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Ordering for:</span>
                    <span className="text-sm font-semibold text-zinc-950 max-w-[180px] truncate">{activeOrg.name}</span>
                    <span className="text-zinc-400">▾</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-72">
                  {memberships.map(m => (
                    <DropdownMenuItem key={m.org_id} onClick={() => setActiveOrg(m.org_id)} data-testid={`switch-org-${m.org_id}`}>
                      <div className="flex items-center gap-2 w-full">
                        <Buildings size={14} className={m.org_id === activeOrg.id ? "text-[#0055FF]" : "text-zinc-400"} weight={m.org_id === activeOrg.id ? "fill" : "regular"} />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-zinc-950">{m.org?.name}</p>
                          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">{m.role.replace("_", " ")} · {m.org?.code}</p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-zinc-200" data-testid="workspace-display">
                <Buildings size={14} className="text-[#0055FF]" weight="fill" />
                <span className="text-sm font-semibold text-zinc-950 max-w-[200px] truncate">{activeOrg.name}</span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">{activeMembership?.role?.replace("_", " ")}</span>
              </div>
            )
          )}

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? "text-[#0055FF] bg-blue-50"
                    : "text-zinc-600 hover:text-zinc-950 hover:bg-zinc-50"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/notifications"
              className="relative p-2 text-zinc-500 hover:text-zinc-950 transition-colors"
              data-testid="notifications-link"
            >
              <Bell size={20} weight="bold" />
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#0055FF] text-white text-[10px] font-bold flex items-center justify-center">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1.5 text-sm text-zinc-600 hover:text-zinc-950 transition-colors" data-testid="user-menu-trigger">
                  <User size={18} weight="bold" />
                  <span className="hidden sm:inline max-w-[120px] truncate">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-zinc-950">{user?.name}</p>
                  <p className="text-xs text-zinc-500">{user?.email}</p>
                  <p className="text-xs font-mono uppercase tracking-wider text-zinc-400 mt-1">{user?.role?.replace("_", " ")}</p>
                </div>
                {!isAdmin && memberships.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <div className="px-3 py-1.5">
                      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">My Clinics</p>
                    </div>
                    {memberships.map(m => (
                      <DropdownMenuItem key={m.org_id} onClick={() => setActiveOrg(m.org_id)} data-testid={`profile-switch-${m.org_id}`}>
                        <Buildings size={14} className={`mr-2 ${m.org_id === activeOrg?.id ? "text-[#0055FF]" : "text-zinc-400"}`} weight={m.org_id === activeOrg?.id ? "fill" : "regular"} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{m.org?.name}</p>
                          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">{m.org?.code}</p>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                <DropdownMenuSeparator />
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/admin/products")} data-testid="menu-manage-products">
                    <GearSix size={16} className="mr-2" /> Manage Products
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <SignOut size={16} className="mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 pb-20 md:pb-8">
        {children}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 w-full border-t border-zinc-200 bg-white pb-safe pt-2 px-4 flex justify-between md:hidden z-50" data-testid="bottom-nav">
        {navItems.map(item => {
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 text-[10px] font-medium transition-colors ${
                isActive(item.path) ? "text-[#0055FF]" : "text-zinc-400"
              }`}
            >
              <Icon size={20} weight={isActive(item.path) ? "fill" : "regular"} />
              {item.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
